import {ChangeEvent, FormEvent, useEffect, useRef, useState} from 'react'
import {Navigate} from 'react-router-dom'
import TopBarProgress from 'react-topbar-progress-indicator'
import {
  AlertCircle,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Download,
  Eye,
  FileText,
  FolderOpen,
  Loader2,
  Search,
  UploadCloud,
} from 'lucide-react'
import {PageTitle} from '../../../_metronic/layout/core'
import {GetPermissions} from '../../modules/auth'
import {
  downloadBlob,
  Fechamento,
  FECHAMENTOS_PERMISSION,
  listFechamentos,
  loadFechamentoImage,
  loadFechamentoPdf,
  MAX_PDF_SIZE,
  uploadFechamento,
} from './fechamentosApi'
import FechamentoViewer from './FechamentoViewer'
import './fechamentos.css'

const formatSize = (size: number) =>
  (size / 1024 / 1024).toLocaleString('pt-BR', {maximumFractionDigits: 1}) + ' MB'
const formatDate = (value: string) => {
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? '' : date.toLocaleDateString('pt-BR')
}

function Cover({closing}: {closing: Fechamento}) {
  const [url, setUrl] = useState('')
  useEffect(() => {
    const controller = new AbortController()
    let objectUrl: string
    loadFechamentoImage(closing.id, 1, true, controller.signal)
      .then((blob) => {
        if (controller.signal.aborted) return
        objectUrl = URL.createObjectURL(blob)
        setUrl(objectUrl)
      })
      .catch(() => {})
    return () => {
      controller.abort()
      if (objectUrl) URL.revokeObjectURL(objectUrl)
    }
  }, [closing.id])
  return url ? (
    <img src={url} alt={'Capa de ' + closing.title} />
  ) : (
    <FileText size={48} aria-hidden='true' />
  )
}

function FechamentosContent() {
  const [items, setItems] = useState<Fechamento[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [listError, setListError] = useState('')
  const [uploadError, setUploadError] = useState('')
  const [success, setSuccess] = useState('')
  const [file, setFile] = useState<File>(null)
  const [title, setTitle] = useState('')
  const [uploading, setUploading] = useState(false)
  const [progress, setProgress] = useState(0)
  const [search, setSearch] = useState('')
  const [searchTerm, setSearchTerm] = useState('')
  const [offset, setOffset] = useState(0)
  const [refresh, setRefresh] = useState(0)
  const [viewer, setViewer] = useState<Fechamento>(null)
  const [downloadingId, setDownloadingId] = useState('')
  const input = useRef<HTMLInputElement>(null)

  useEffect(() => {
    const timeout = window.setTimeout(() => setSearchTerm(search.trim()), 300)
    return () => window.clearTimeout(timeout)
  }, [search])

  useEffect(() => {
    const controller = new AbortController()
    setLoading(true)
    setListError('')
    listFechamentos(searchTerm, offset, controller.signal)
      .then((data) => {
        if (controller.signal.aborted) return
        setItems(data.items)
        setTotal(data.total)
        setLoading(false)
      })
      .catch((error) => {
        if (controller.signal.aborted) return
        setListError(error.message)
        setLoading(false)
      })
    return () => controller.abort()
  }, [searchTerm, offset, refresh])

  const chooseFile = (event: ChangeEvent<HTMLInputElement>) => {
    const selected = event.target.files?.[0]
    setUploadError('')
    setSuccess('')
    setFile(null)
    if (!selected) return
    if (!/\.pdf$/i.test(selected.name)) {
      setUploadError('Selecione um arquivo PDF.')
      event.target.value = ''
      return
    }
    if (!selected.size || selected.size > MAX_PDF_SIZE) {
      setUploadError('O PDF deve ter conteúdo e tamanho de até 30 MB.')
      event.target.value = ''
      return
    }
    setFile(selected)
    setTitle(selected.name.replace(/\.pdf$/i, '').slice(0, 180))
  }

  const submit = async (event: FormEvent) => {
    event.preventDefault()
    if (!file || uploading) return
    setUploading(true)
    setUploadError('')
    setSuccess('')
    setProgress(0)
    try {
      const result = await uploadFechamento(file, title, setProgress)
      setSuccess('Fechamento salvo. ' + result.pageCount + ' páginas convertidas em imagens.')
      setFile(null)
      setTitle('')
      if (input.current) input.current.value = ''
      setSearch('')
      setSearchTerm('')
      setOffset(0)
      setRefresh((value) => value + 1)
      setViewer(result)
    } catch (error) {
      setUploadError(error.message)
    } finally {
      setUploading(false)
    }
  }

  const downloadPdf = async (closing: Fechamento) => {
    setDownloadingId(closing.id)
    setListError('')
    try {
      downloadBlob(await loadFechamentoPdf(closing.id), closing.fileName)
    } catch (error) {
      setListError(error.message)
    } finally {
      setDownloadingId('')
    }
  }

  return (
    <div className='fechamentos-page'>
      <PageTitle>Fechamentos</PageTitle>
      <header className='fechamentos-heading'>
        <span className='fechamentos-eyebrow'>ADMINISTRATIVO</span>
        <h1>Fechamentos</h1>
        <p>Centralize os relatórios de fechamento e visualize cada página em alta qualidade.</p>
      </header>
      <section className='fechamentos-upload' aria-labelledby='fechamentos-upload-title'>
        <div className='fechamentos-upload-heading'>
          <span className='fechamentos-upload-icon'>
            <UploadCloud size={25} />
          </span>
          <div>
            <h2 id='fechamentos-upload-title'>Novo fechamento</h2>
            <p>Envie o PDF para gerar uma imagem PNG de cada página.</p>
          </div>
        </div>
        <form onSubmit={submit}>
          <div className='fechamentos-upload-fields'>
            <div className='fechamentos-field fechamentos-file-field'>
              <label htmlFor='fechamentos-file'>Arquivo PDF</label>
              <input
                ref={input}
                id='fechamentos-file'
                type='file'
                accept='.pdf,application/pdf'
                onChange={chooseFile}
                disabled={uploading}
                aria-describedby='fechamentos-file-help'
              />
              <small id='fechamentos-file-help'>
                {file
                  ? file.name + ' · ' + formatSize(file.size)
                  : 'Até 30 MB e 100 páginas. PDF sem senha.'}
              </small>
            </div>
            <div className='fechamentos-field'>
              <label htmlFor='fechamentos-title'>Título do fechamento</label>
              <input
                id='fechamentos-title'
                type='text'
                value={title}
                onChange={(event) => setTitle(event.target.value)}
                maxLength={180}
                placeholder='Ex.: Fechamento D&AI Setembro 2026'
                disabled={uploading}
              />
            </div>
            <button
              type='submit'
              className='fechamentos-button primary'
              disabled={!file || uploading}
            >
              {uploading ? (
                <Loader2 size={18} className='fechamentos-spin' />
              ) : (
                <UploadCloud size={18} />
              )}
              {uploading ? 'Processando...' : 'Enviar PDF'}
            </button>
          </div>
          {uploading && (
            <div className='fechamentos-progress' role='status' aria-live='polite'>
              <div>
                <span style={{width: progress + '%'}} />
              </div>
              <p>
                {progress < 100
                  ? 'Enviando PDF: ' + progress + '%'
                  : 'Convertendo páginas em imagens. Aguarde a conclusão.'}
              </p>
            </div>
          )}
        </form>
        {uploadError && (
          <div className='fechamentos-message error' role='alert'>
            <AlertCircle size={18} />
            {uploadError}
          </div>
        )}
        {success && (
          <div className='fechamentos-message success' role='status'>
            <CheckCircle2 size={18} />
            {success}
          </div>
        )}
      </section>
      <section aria-labelledby='fechamentos-history-title'>
        <div className='fechamentos-history-heading'>
          <h2 id='fechamentos-history-title'>
            Fechamentos enviados <span>{total}</span>
          </h2>
          <div className='fechamentos-search'>
            <Search size={18} />
            <input
              type='search'
              aria-label='Buscar fechamentos'
              placeholder='Buscar pelo título ou arquivo'
              maxLength={180}
              value={search}
              onChange={(event) => {
                setSearch(event.target.value)
                setOffset(0)
              }}
            />
          </div>
        </div>
        {listError && (
          <div className='fechamentos-message error' role='alert'>
            <AlertCircle size={18} />
            {listError}
            <button type='button' onClick={() => setRefresh((value) => value + 1)}>
              Tentar novamente
            </button>
          </div>
        )}
        {loading ? (
          <div className='fechamentos-empty' role='status'>
            <Loader2 size={30} className='fechamentos-spin' />
            <p>Carregando fechamentos...</p>
          </div>
        ) : !items.length && !listError ? (
          <div className='fechamentos-empty'>
            <FolderOpen size={42} />
            <h3>
              {searchTerm ? 'Nenhum fechamento encontrado' : 'Seu primeiro fechamento começa aqui'}
            </h3>
            <p>
              {searchTerm
                ? 'Tente buscar por outro título.'
                : 'Envie um PDF para visualizar e exportar suas páginas.'}
            </p>
          </div>
        ) : (
          <div className='fechamentos-grid'>
            {items.map((closing) => (
              <article className='fechamentos-card' key={closing.id}>
                <button
                  type='button'
                  className='fechamentos-cover'
                  onClick={() => setViewer(closing)}
                  aria-label={'Visualizar ' + closing.title}
                >
                  <Cover closing={closing} />
                  <span>
                    {closing.pageCount} {closing.pageCount === 1 ? 'página' : 'páginas'}
                  </span>
                </button>
                <div className='fechamentos-card-body'>
                  <h3 title={closing.title}>{closing.title}</h3>
                  <p className='fechamentos-filename' title={closing.fileName}>
                    {closing.fileName}
                  </p>
                  <p className='fechamentos-card-meta'>
                    {formatDate(closing.createdAt)} · {formatSize(closing.fileSize)}
                  </p>
                  <p className='fechamentos-author' title={closing.createdByName}>
                    Enviado por {closing.createdByName}
                  </p>
                  <div className='fechamentos-card-actions'>
                    <button
                      type='button'
                      className='fechamentos-button primary'
                      onClick={() => setViewer(closing)}
                    >
                      <Eye size={17} /> Visualizador
                    </button>
                    <button
                      type='button'
                      className='fechamentos-button secondary'
                      onClick={() => downloadPdf(closing)}
                      disabled={!!downloadingId}
                      aria-label={'Baixar PDF de ' + closing.title}
                    >
                      {downloadingId === closing.id ? (
                        <Loader2 size={17} className='fechamentos-spin' />
                      ) : (
                        <Download size={17} />
                      )}{' '}
                      PDF
                    </button>
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}
        {total > 12 && (
          <nav className='fechamentos-pagination' aria-label='Paginação dos fechamentos'>
            <button
              type='button'
              className='fechamentos-button secondary'
              disabled={offset === 0 || loading}
              onClick={() => setOffset((value) => Math.max(0, value - 12))}
            >
              <ChevronLeft size={16} /> Anterior
            </button>
            <span>
              Página {Math.floor(offset / 12) + 1} de {Math.ceil(total / 12)}
            </span>
            <button
              type='button'
              className='fechamentos-button secondary'
              disabled={offset + 12 >= total || loading}
              onClick={() => setOffset((value) => value + 12)}
            >
              Próxima <ChevronRight size={16} />
            </button>
          </nav>
        )}
      </section>
      {viewer && (
        <FechamentoViewer key={viewer.id} closing={viewer} onClose={() => setViewer(null)} />
      )}
    </div>
  )
}

export default function FechamentosPage() {
  const permissions = GetPermissions()
  if (!permissions?.length) return <TopBarProgress />
  if (!permissions.some((permission) => permission.name === FECHAMENTOS_PERMISSION))
    return <Navigate to='/error/404' replace />
  return <FechamentosContent />
}
